import type { PlayerId } from './ids';

/** Horizontal answer zones, mirrored to match the child's view (MVP notes §5). */
export type BodyZone = 'left' | 'center' | 'right';

export const BODY_ZONES: readonly BodyZone[] = ['left', 'center', 'right'] as const;

export interface PlayerEnteredFrameEvent {
  type: 'PLAYER_ENTERED_FRAME';
  playerId: PlayerId;
  at: number;
}

export interface PlayerLeftFrameEvent {
  type: 'PLAYER_LEFT_FRAME';
  playerId: PlayerId;
  at: number;
}

export interface PlayerZoneChangedEvent {
  type: 'PLAYER_ZONE_CHANGED';
  playerId: PlayerId;
  zone: BodyZone;
  at: number;
}

export interface PlayerJumpedEvent {
  type: 'PLAYER_JUMPED';
  playerId: PlayerId;
  at: number;
}

export interface PlayerCrouchedEvent {
  type: 'PLAYER_CROUCHED';
  playerId: PlayerId;
  at: number;
}

export interface PlayerStoodEvent {
  type: 'PLAYER_STOOD';
  playerId: PlayerId;
  at: number;
}

export interface PlayerRaisedHandsEvent {
  type: 'PLAYER_RAISED_HANDS';
  playerId: PlayerId;
  at: number;
}

export interface PlayerLoweredHandsEvent {
  type: 'PLAYER_LOWERED_HANDS';
  playerId: PlayerId;
  at: number;
}

export interface PlayerBecameStillEvent {
  type: 'PLAYER_BECAME_STILL';
  playerId: PlayerId;
  at: number;
}

export interface PlayerMovedEvent {
  type: 'PLAYER_MOVED';
  playerId: PlayerId;
  at: number;
}

/**
 * The complete semantic input vocabulary (master prompt §21). Keyboard and camera adapters
 * emit these identical events; nothing downstream sees raw frames or landmarks.
 */
export type ThriveInputEvent =
  | PlayerEnteredFrameEvent
  | PlayerLeftFrameEvent
  | PlayerZoneChangedEvent
  | PlayerJumpedEvent
  | PlayerCrouchedEvent
  | PlayerStoodEvent
  | PlayerRaisedHandsEvent
  | PlayerLoweredHandsEvent
  | PlayerBecameStillEvent
  | PlayerMovedEvent;

export type ThriveInputEventType = ThriveInputEvent['type'];

export type ThriveInputEventOf<TType extends ThriveInputEventType> = Extract<
  ThriveInputEvent,
  { type: TType }
>;

export const THRIVE_INPUT_EVENT_TYPES: readonly ThriveInputEventType[] = [
  'PLAYER_ENTERED_FRAME',
  'PLAYER_LEFT_FRAME',
  'PLAYER_ZONE_CHANGED',
  'PLAYER_JUMPED',
  'PLAYER_CROUCHED',
  'PLAYER_STOOD',
  'PLAYER_RAISED_HANDS',
  'PLAYER_LOWERED_HANDS',
  'PLAYER_BECAME_STILL',
  'PLAYER_MOVED',
] as const;
